import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Careers.scss';
import Seo from '../../../components/reusable/Seo/Seo.js';
import PageHero from '../../../components/ui/PageHero.js';
import Reveal from '../../../components/ui/Reveal.js';
import ContactForm from '../../../components/reusable/ContactForm/ContactForm.js';
import physicianPhoto from '../../../assets/images/stock/careers-physician.jpg';

/* Preset for the ContactForm "Which describes you best?" select.
   Must be an EXACT value from optionsForWhatsDescribesYou. */
const PHYSICIAN_OPTION = 'Physician (PM&R)';

/* ---- Data ---- */

const FACTS = [
  ['Physician-owned', 'and physician-built'],
  ['Operating since', '2021'],
  ['Multi-unit', 'coverage across Virginia'],
  ['Expanding', 'to new markets'],
];

const PATHS = [
  {
    t: 'The employed job',
    d: 'Stable and simple. But the upside is capped, the playbook is someone else’s, and the ceiling shows up early in your career.',
  },
  {
    t: 'The national group',
    d: 'Scale without a stake. The economics, and the decisions that shape how you practice, belong to the corporation.',
  },
  {
    t: 'Going independent alone',
    d: 'Real ownership, if you can also build billing, credentialing, workflows, and hospital relationships from scratch, all while carrying a full census.',
  },
];

const OS_ITEMS = [
  {
    n: '01',
    t: 'Charge capture that minimizes clicks',
    d: 'A charge-capture and patient-management platform built inside the practice, engineered to keep the administrative layer of your day thin.',
  },
  {
    n: '02',
    t: 'Documentation that protects your energy',
    d: 'An assistive documentation tool that speeds your notes, preserves cognitive energy for complex patients, and builds in the language IRF reimbursement requires.',
  },
  {
    n: '03',
    t: 'Written workflows and admission criteria',
    d: 'Clinical workflows and admission criteria developed over years of actually running units. You inherit answers, not blank pages.',
  },
  {
    n: '04',
    t: 'Full administrative support',
    d: 'Scheduling, credentialing, onboarding, and billing handled, so the operating burden of private practice doesn’t land on you.',
  },
  {
    n: '05',
    t: 'APP support when volume justifies it',
    d: 'Advanced practice providers hired, trained, and supported as census grows.',
  },
  {
    n: '06',
    t: 'Mentorship and negotiation support',
    d: 'Direct mentorship from an experienced physician operator, including hospital negotiations, so call doesn’t fall disproportionately on the rehab physician.',
  },
];

const NINETY = [
  {
    range: 'Days 1–30',
    t: 'Structured onboarding',
    d: 'Workflow training on the systems you’ll use every day: charge capture, documentation, written admission criteria, taught by the people who built and refined them.',
  },
  {
    range: 'Days 31–60',
    t: 'Unit immersion',
    d: 'On the unit with real patients, real census, and documentation feedback, with direct access to experienced operators while you calibrate to how VRP runs rehab.',
  },
  {
    range: 'Days 61–90',
    t: 'Progressive ownership',
    d: 'You take progressively full ownership of your practice and your unit, with the operating system, and the group, behind you.',
  },
];

const FIT_YES = [
  'You want ownership, both clinical and economic, not just a schedule.',
  'You care how the unit runs, not just how your list looks.',
  'You want modern tools and written systems behind your judgment.',
  'You want a path to grow: new units, new markets, real leadership.',
];

const FIT_NO = [
  'You’re looking for the lowest-responsibility version of PM&R.',
  'You want someone else to own every decision that shapes your practice.',
  'You’d rather not be measured by how your unit actually performs.',
];

const GROWTH = [
  {
    t: 'Grow into new markets',
    d: 'We’re expanding into new markets, and doing it deliberately. Physicians who join now help shape where VRP goes next, and can lead what we build there.',
  },
  {
    t: 'Earn medical directorship',
    d: 'A real path to directing a unit, with written criteria, genuine authority, and accountability for how the unit performs.',
  },
  {
    t: 'Build alongside an operator',
    d: 'Work directly with an experienced physician operator who has built this practice unit by unit, and is building its next decade.',
  },
];

const PMR_COVERAGE = {
  eyebrow: 'Current opening',
  title: 'Weekend & Vacation Coverage - PM&R (Richmond, VA)',
  lede: 'Virginia Rehab Physicians is seeking board-certified or board-eligible PM&R physicians for PRN weekend and vacation coverage in the greater Richmond area: a flexible, as-needed opportunity for physiatrists looking to supplement their practice with locum-style coverage.',
  responsibilities: [
    'Provide physiatric coverage on weekends and during physician vacations.',
    'Rounding, consults, and admissions as needed, across VRP’s inpatient rehab units and acute care consult services.',
  ],
  qualifications: [
    'MD or DO.',
    'Board-certified or board-eligible in Physical Medicine & Rehabilitation.',
    'Active Virginia medical license, or eligibility to obtain one.',
    'Current DEA registration.',
  ],
};

/* ---- Page ---- */

function Careers() {
  const { hash } = useLocation();

  /* React Router doesn't scroll to hash targets (e.g. the footer's
     /careers#first-90-days deep link), handle it here. */
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [hash]);

  return (
    <div className="careers">
      <Seo
        title="Careers at VRP"
        description="The income and autonomy of private practice for PM&R physicians, without the tradeoffs, backed by custom software, written workflows, full admin support, and direct mentorship. You bring the clinical ownership. We bring the operating system."
        path="/careers"
      />

      {/* 1 ---- Hero. PageHero's primary prop renders a react-router <Link>,
          which doesn't handle same-page hash anchors well, so the CTA lives
          in the strip below as a native <a href="#express-interest">. */}
      <PageHero
        eyebrow="Careers at VRP"
        title={<>You bring the clinical ownership. <em>We bring the operating system.</em></>}
        lede={
          <>
            The income and autonomy of private practice for PM&amp;R physicians,
            without the tradeoffs the usual models force, and without building
            the infrastructure alone.
          </>
        }
      />

      <div className="careers-strip u-grain">
        <div className="u-container careers-strip__inner">
          <ul className="careers-strip__facts">
            {FACTS.map(([strong, rest]) => (
              <li key={strong}>
                <strong>{strong}</strong> {rest}
              </li>
            ))}
          </ul>
          <div className="careers-strip__cta">
            <a className="btn btn--gold" href="#express-interest">
              Express interest <span className="btn__arrow" aria-hidden="true">→</span>
            </a>
            <a className="btn btn--ghost-light" href="#first-90-days">
              Your first 90 days
            </a>
          </div>
        </div>
      </div>

      {/* 2 ---- Without the tradeoff */}
      <section className="u-section careers-fourth">
        <div className="u-container">
          <Reveal className="careers-head">
            <span className="u-eyebrow">A better way</span>
            <h2>Every model asks you to give something up.</h2>
            <p className="careers-head__lead">
              Upside, control, or your time: the prominent ways to practice
              rehabilitation medicine each trade away something that matters.
              We built VRP so you don&rsquo;t have to.
            </p>
          </Reveal>

          <div className="careers-fourth__paths">
            {PATHS.map((p, i) => (
              <Reveal as="article" key={p.t} className="careers-fourth__path" delay={0.06 * i}>
                <span className="careers-fourth__num">{i + 1}</span>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="careers-fourth__vrp u-grain" delay={0.15}>
            <div className="careers-fourth__vrp-copy">
              <span className="careers-fourth__vrp-tag">The one that doesn&rsquo;t</span>
              <h3>Private-practice upside, with real infrastructure behind you.</h3>
              <p>
                We&rsquo;ve run inpatient rehab units across Virginia since
                2021, and built, in-house, the operating system we wished
                someone had handed us. Join, and you practice with the income
                and autonomy of ownership, on systems designed by the physicians
                who use them every day. The upside isn&rsquo;t less
                responsibility. It&rsquo;s better infrastructure, so the
                responsibility is worth carrying.
              </p>
            </div>
            <figure className="careers-fourth__vrp-media">
              <img
                className="careers-fourth__vrp-img"
                src={physicianPhoto}
                alt="A rehabilitation physician in a clinical setting"
                loading="lazy"
              />
            </figure>
          </Reveal>
        </div>
      </section>

      {/* 3 ---- What you get (dark) */}
      <section className="careers-os u-grain">
        <div className="u-container careers-os__grid">
          <div className="careers-os__intro">
            <Reveal>
              <span className="u-eyebrow u-eyebrow--signal">The operating system</span>
              <h2>What you get <em>on day one.</em></h2>
              <p className="careers-os__lead">
                Everything a high-performing private practice needs, already
                built, already running, refined on real units.
              </p>
            </Reveal>
          </div>
          <ol className="careers-os__list">
            {OS_ITEMS.map((g, i) => (
              <Reveal as="li" key={g.n} delay={0.04 * i} className="careers-os__item">
                <span className="careers-os__num">{g.n}</span>
                <div>
                  <h3>{g.t}</h3>
                  <p>{g.d}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* 4 ---- First 90 days */}
      <section className="u-section careers-90" id="first-90-days">
        <div className="u-container">
          <Reveal className="careers-head">
            <span className="u-eyebrow">Onboarding</span>
            <h2>Your first 90 days.</h2>
            <p className="careers-head__lead">
              Clinical ownership done well is earned in stages, so we onboard
              deliberately, with experienced operators beside you the whole way.
            </p>
          </Reveal>

          <ol className="careers-90__track">
            {NINETY.map((s, i) => (
              <Reveal as="li" key={s.range} className="careers-90__step" delay={0.08 * i}>
                <span className="careers-90__marker" aria-hidden="true" />
                <span className="careers-90__range">{s.range}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </Reveal>
            ))}
          </ol>

          <Reveal className="careers-90__close" delay={0.2}>
            <p>Autonomy is the destination, not the starting point.</p>
          </Reveal>
        </div>
      </section>

      {/* 5 ---- Who this is for */}
      <section className="u-section careers-fit">
        <div className="u-container">
          <Reveal className="careers-head">
            <span className="u-eyebrow">Who this is for</span>
            <h2>We’re selective. You should be too.</h2>
            <p className="careers-head__lead">
              VRP stays small on purpose, and every physician here shapes how the
              group runs. That only works when the fit is honest in both
              directions.
            </p>
          </Reveal>

          <div className="careers-fit__cols">
            <Reveal as="article" className="careers-fit__col careers-fit__col--yes">
              <h3>You’ll do well here if…</h3>
              <ul>
                {FIT_YES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal as="article" className="careers-fit__col careers-fit__col--no" delay={0.08}>
              <h3>This isn’t the right fit if…</h3>
              <ul>
                {FIT_NO.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="careers-fit__note">
                Neither answer is wrong. Plenty of good careers are built on
                the other side of this line. They just aren’t built here.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6 ---- Where this goes */}
      <section className="u-section careers-growth">
        <div className="u-container">
          <Reveal className="careers-head">
            <span className="u-eyebrow">Where this goes</span>
            <h2>A practice you can grow into.</h2>
          </Reveal>
          <div className="careers-growth__grid">
            {GROWTH.map((g, i) => (
              <Reveal as="article" key={g.t} className="careers-growth__card" delay={0.06 * i}>
                <h3>{g.t}</h3>
                <p>{g.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6b ---- Current opening: PM&R weekend & vacation coverage (Richmond) */}
      <section className="u-section careers-pmr" id="pmr-coverage">
        <div className="u-container">
          <Reveal className="careers-head">
            <span className="u-eyebrow">{PMR_COVERAGE.eyebrow}</span>
            <h2>{PMR_COVERAGE.title}</h2>
            <p className="careers-head__lead">{PMR_COVERAGE.lede}</p>
          </Reveal>

          <Reveal className="careers-pmr__cols" delay={0.08}>
            <div className="careers-pmr__col">
              <h3>Responsibilities</h3>
              <ul>
                {PMR_COVERAGE.responsibilities.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="careers-pmr__col">
              <h3>Qualifications</h3>
              <ul>
                {PMR_COVERAGE.qualifications.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal className="careers-pmr__cta" delay={0.14}>
            <a className="btn btn--gold" href="#express-interest">
              Express interest <span className="btn__arrow" aria-hidden="true">→</span>
            </a>
          </Reveal>
        </div>
      </section>

      {/* 7 ---- Express interest */}
      <section className="u-section careers-apply" id="express-interest">
        <div className="u-container u-container--narrow">
          <Reveal className="careers-head careers-head--center">
            <span className="u-eyebrow">Express interest</span>
            <h2>Start the conversation.</h2>
            <p className="careers-head__lead">
              Tell us where you are in training or practice and what you want
              to build. Your note goes to a physician who will actually read
              it. This is a conversation, not a recruiter funnel.
            </p>
          </Reveal>
          <Reveal className="careers-apply__card" delay={0.08}>
            <ContactForm className="careers-apply__form" defaultDescription={PHYSICIAN_OPTION} />
          </Reveal>
          <Reveal className="careers-apply__alt" delay={0.12}>
            <p>
              Prefer email? Reach us through our <Link to="/contact">contact page</Link>.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Careers;
